import { useEffect, useState } from 'react';

export interface KeyboardViewport {
  /** Visible height above the on-screen keyboard, in CSS px. */
  height: number;
  /** Top of the visible area in layout-viewport coordinates. */
  offsetTop: number;
}

/**
 * The visible region of the page while an on-screen keyboard is open, or null
 * when no keyboard is covering the page.
 *
 * iOS Safari does not resize the layout viewport when the keyboard opens; it
 * draws the keyboard over it. A `position: fixed` dialog centred on the layout
 * viewport therefore keeps its centre where it was and its lower half, input
 * included, ends up under the keyboard. `window.visualViewport` is the only
 * signal for the area the reader can still see, so a dialog that holds a text
 * field re-centres itself on it.
 */
export function useKeyboardViewport(enabled = true): KeyboardViewport | null {
  const [viewport, setViewport] = useState<KeyboardViewport | null>(null);

  useEffect(() => {
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!enabled || !vv) {
      setViewport(null);
      return;
    }

    const update = () => {
      const covered = window.innerHeight - vv.height;
      // Pinch-zoom also shrinks the visual viewport; a keyboard is the case
      // where the scale is still 1 and a large slice of the height is gone.
      if (vv.scale <= 1.01 && covered > 120) {
        setViewport({ height: vv.height, offsetTop: vv.offsetTop });
      } else {
        setViewport(null);
      }
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return viewport;
}
